import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUI } from "@/context/UIContext";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Play, Plus, Check, X, Film } from "lucide-react";

export function DetailModal() {
  const { detail, closeDetail, myListIds, toggleMyList } = useUI();
  const navigate = useNavigate();
  const item = detail;
  const open = !!item;
  const inList = item && myListIds.has(item.id);
  const [season, setSeason] = useState(1);

  useEffect(() => { if (item) setSeason(1); }, [item?.id]);

  const isSeries = item?.type === "series" && item?.episodes?.length > 0;
  const seasonEps = isSeries ? (item.episodes.find((s) => s.season === season)?.episodes || []) : [];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && closeDetail()}>
      <DialogContent
        data-testid="detail-modal"
        className="max-w-3xl p-0 overflow-hidden bg-[#121824] border-white/10 text-white gap-0 max-h-[90vh] overflow-y-auto no-scrollbar"
      >
        {item && (
          <>
            <DialogTitle className="sr-only">{item.title}</DialogTitle>
            <div className="relative h-64 sm:h-80">
              <img src={item.backdrop} alt={item.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#121824] via-[#121824]/30 to-transparent" />
              <button onClick={closeDetail} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/60 flex items-center justify-center hover:bg-black/80">
                <X size={18} />
              </button>
              <div className="absolute bottom-5 left-6 right-6">
                {item.is_original && (
                  <div className="text-xs tracking-[0.3em] text-slate-200 mb-2 font-semibold">
                    <span className="text-[var(--nik-gold)]">K</span> KALAMPRIME ORIGINAL
                  </div>
                )}
                <h2 className="font-display text-3xl sm:text-4xl font-extrabold">{item.title}</h2>
                <div className="flex items-center gap-3 mt-4 flex-wrap">
                  <button
                    onClick={() => { closeDetail(); navigate(`/watch/${item.id}`); }}
                    data-testid="modal-watch-btn"
                    className="flex items-center gap-2 bg-white text-black font-semibold px-6 py-2.5 rounded-lg hover:bg-slate-200 transition-colors"
                  >
                    <Play size={18} fill="currentColor" /> Watch Now
                  </button>
                  <button
                    onClick={() => { closeDetail(); navigate(`/watch/${item.id}?trailer=1`); }}
                    className="flex items-center gap-2 bg-white/10 border border-white/20 px-4 py-2.5 rounded-lg hover:bg-white/20 transition-colors"
                  >
                    <Film size={17} /> Trailer
                  </button>
                  <button
                    onClick={() => toggleMyList(item)}
                    className="w-11 h-11 rounded-full border border-white/30 flex items-center justify-center hover:border-white bg-black/30"
                  >
                    {inList ? <Check size={19} /> : <Plus size={19} />}
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-3 text-sm mb-4">
                    {item.match > 0 && <span className="text-[var(--nik-glow)] font-bold font-mono">{item.match}% Match</span>}
                    <span>{item.year}</span>
                    <span className="border border-white/30 px-1.5 rounded text-xs">{item.rating}</span>
                    <span className="text-slate-400">{item.duration}</span>
                  </div>
                  <p className="text-slate-200 leading-relaxed">{item.description}</p>
                  {item.poster2 && (
                    <div className="mt-4" data-testid="detail-poster2">
                      <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">More</div>
                      <img src={item.poster2} alt={`${item.title} poster 2`} className="rounded-lg border border-white/10 max-h-64 object-cover" />
                    </div>
                  )}
                </div>
                <div className="md:w-56 text-sm space-y-3">
                  <div>
                    <span className="text-slate-500">Cast: </span>
                    <span className="text-slate-300">{(item.cast || []).join(", ")}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Genres: </span>
                    <span className="text-slate-300">{(item.genres || []).join(", ")}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Languages: </span>
                    <span className="text-slate-300">{(item.languages || []).join(", ")}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Type: </span>
                    <span className="text-slate-300 capitalize">{item.type}</span>
                  </div>
                </div>
              </div>
            </div>

            {isSeries && (
              <div className="px-6 pb-6" data-testid="modal-episodes">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-display text-lg font-bold text-white">Episodes</h3>
                  {item.episodes.length > 1 && (
                    <select
                      value={season}
                      onChange={(e) => setSeason(parseInt(e.target.value))}
                      className="bg-[#0B0E14] border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white outline-none"
                    >
                      {item.episodes.map((s) => (
                        <option key={s.season} value={s.season}>Season {s.season}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="space-y-2">
                  {seasonEps.map((e) => (
                    <button
                      key={e.number}
                      data-testid={`modal-episode-${season}-${e.number}`}
                      onClick={() => { closeDetail(); navigate(`/watch/${item.id}?s=${season}&e=${e.number}`); }}
                      className="w-full flex gap-3 p-2 rounded-xl text-left hover:bg-white/5 border border-transparent hover:border-white/10 transition-colors group"
                    >
                      <div className="relative w-32 h-20 rounded-lg overflow-hidden shrink-0">
                        <img src={e.thumbnail} alt="" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Play size={22} className="text-white" fill="currentColor" />
                        </div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-slate-500 font-mono text-sm">{e.number}</span>
                          <span className="text-sm font-semibold text-white truncate">{e.title}</span>
                        </div>
                        <div className="text-xs text-slate-500 font-mono mt-0.5">{e.duration}</div>
                        <div className="text-xs text-slate-400 line-clamp-2 mt-1">{e.description}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
